import { v4 as uuidv4 } from 'uuid';
import { Tenancy_Node_Type_Enum_Ps_Enum } from '@pypestream/api-services';

import { proStudioService, sendProStudioEvent } from '../../xstate/app.xstate';
import { ProStudioContext } from '../../xstate/prostudio.xstate.types';
import { defaultRowData, NodeColumns, type TableRow } from '../table-store';

export interface Task {
  id: string;
  name: string;
  description?: string;
}

export interface CreateTaskNodeParams {
  taskName: string;
  taskDescription: string;
  taskId: string;
  solutionVersionId: string;
  existingRows: TableRow[];
  order?: number;
}

/**
 * Creates a new task node with the specified parameters
 */
export function createTaskNode(params: CreateTaskNodeParams): TableRow {
  const {
    taskName,
    taskDescription,
    taskId,
    solutionVersionId,
    existingRows,
    order,
  } = params;

  const maxNodeNumber = Math.max(
    ...existingRows.map((r) => r.nodeNumber || 0),
    0
  );
  return {
    ...defaultRowData,
    [NodeColumns.nodeNumber]: maxNodeNumber + 1,
    [NodeColumns.nodeType]: Tenancy_Node_Type_Enum_Ps_Enum.D,
    [NodeColumns.nodeName]: taskName,
    [NodeColumns.order]: order ?? 0,
    [NodeColumns.id]: uuidv4(),
    [NodeColumns.solutionVersionId]: solutionVersionId,
    [NodeColumns.utterances]: taskDescription,
    [NodeColumns.description]: '',
    [NodeColumns.settings]: {
      taskId,
    },
  };
}

/**
 * Updates a task node with new name and description
 */
export function updateTaskNode(
  taskNode: TableRow,
  updates: { name: string; description?: string }
): Partial<TableRow> {
  return {
    ...taskNode,
    [NodeColumns.nodeName]: updates.name,
    [NodeColumns.utterances]: updates.description,
  };
}

/**
 * Handles task creation response by updating the task node
 */
export function handleTaskCreationResponse(
  _taskNode: TableRow,
  taskDescription?: string
): Partial<TableRow> {
  return {
    [NodeColumns.utterances]: taskDescription,
  };
}

/**
 * Checks if a row is a task row based on the solution data
 */
export function isTaskRow(taskId: string): boolean {
  const { draftVersionSolutionData } = proStudioService.getSnapshot().context;
  const isTask =
    draftVersionSolutionData?.tasks?.some((task) => task.id === taskId) ||
    false;
  return isTask;
}

/**
 * Updates the solution data for a task row
 */
export function updateSolutionForTaskRow(
  taskId: string,
  updates: Partial<TableRow>
): void {
  const { draftVersionSolutionData } = proStudioService.getSnapshot().context;

  if (!draftVersionSolutionData?.id) {
    console.warn('No solution version ID available for task update');
    return;
  }

  const task = draftVersionSolutionData.tasks?.find(
    (existingTask) => existingTask.id === taskId
  );
  if (!task) {
    console.warn('Task not found in solution data for row:', taskId);
    return;
  }

  // Determine what the new values would be and check if they've changed
  const currentName = task.name || '';
  const currentDescription = task.description || '';
  let hasChanges = false;
  let newName: string | undefined;
  let newDescription: string | undefined;

  if (updates.nodeName !== undefined) {
    newName = String(updates.nodeName);
    if (newName !== currentName) {
      hasChanges = true;
    }
  }

  if (updates.utterances !== undefined) {
    newDescription = String(updates.utterances);
    if (newDescription !== currentDescription) {
      hasChanges = true;
    }
  }

  if (!hasChanges) {
    return;
  }

  const finalName = newName !== undefined ? newName : currentName;
  const finalDescription =
    newDescription !== undefined ? newDescription : currentDescription;

  const version = {
    ...draftVersionSolutionData,
    solutionId: undefined,
    llmCredential: undefined,
    tasks:
      draftVersionSolutionData.tasks?.map((existingTask) =>
        existingTask.id === taskId
          ? {
              id: existingTask.id,
              name: finalName,
              description: finalDescription,
            }
          : {
              id: existingTask.id,
              name: existingTask.name || '',
              description: existingTask.description,
            }
      ) || [],
    files: draftVersionSolutionData.files?.map((file) => ({
      url: file.url,
      mimeType: file.mimeType || '',
      originalName: file.originalName || '',
      publicId: file.publicId,
      size: file.size || 0,
    })),
  };

  sendProStudioEvent({
    type: 'updateSolution',
    payload: {
      version,
      cb: () => {},
    },
  });
}

/**
 * Deletes a task from the solution data
 */
export function deleteSolutionTask(taskId: string): void {
  const { draftVersionSolutionData } = proStudioService.getSnapshot().context;

  if (!draftVersionSolutionData?.id) {
    console.warn('No solution version ID available for task deletion');
    return;
  }

  const taskExists = draftVersionSolutionData.tasks?.some(
    (task) => task.id === taskId
  );

  if (!taskExists) {
    return;
  }

  const version = {
    ...draftVersionSolutionData,
    solutionId: undefined,
    llmCredential: undefined,
    tasks:
      draftVersionSolutionData.tasks
        ?.filter((task) => task.id !== taskId)
        .map((task) => ({
          id: task.id,
          name: task.name || '',
          description: task.description,
        })) || [],
    files: draftVersionSolutionData.files?.map((file) => ({
      url: file.url,
      mimeType: file.mimeType || '',
      originalName: file.originalName || '',
      publicId: file.publicId,
      size: file.size || 0,
    })),
  };

  sendProStudioEvent({
    type: 'updateSolution',
    payload: {
      version,
      cb: () => {},
    },
  });
}

/**
 * Gets task data from solution data
 */
export function getTasksFromSolutionData(
  updatedSolutionData?: ProStudioContext['draftVersionSolutionData']
): Task[] {
  const { draftVersionSolutionData } = proStudioService.getSnapshot().context;
  const solutionData = updatedSolutionData || draftVersionSolutionData;

  if (!solutionData?.tasks || solutionData.tasks.length === 0) {
    return [];
  }

  return solutionData.tasks.map((task) => ({
    id: task.id || '',
    name: task.name || '',
    description: task.description,
  }));
}
